import { useState, useRef, useEffect, useCallback, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import Tooltip from './Tooltip';
import NewRequestKindMenu from './NewRequestKindMenu';
import RequestTabBadge from './RequestTabBadge';
import { dropdownSide } from '../../utils/dropdownSide';
import { useTranslation } from 'react-i18next';
import {
  X,
  Plus,
  Search,
  Copy,
  Layers,
  Braces,
  Database,
  Kanban,
  FileText,
  FolderKanban,
  ChevronDown,
  ListX,
} from 'lucide-react';
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors, closestCenter,
} from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import {
  buildDefaultTabStripOrder,
  getTabStripSortableId,
} from '../../utils/workspaceTabPersistence';
import CollectionTabBadge from './CollectionTabBadge';
import {
  DNDKIT_MODIFIERS,
  TAB_STRIP_HEIGHT,
  getSortableTabId,
  getTabMethod,
  getTabTitle,
  getTabTooltip,
  isRequestTabCloseable,
  resolveTabFromEntry,
  tooltipContent,
} from './tabStrip/tabStripModel';
import { SortableTab, TabCloseIcon, TabComponent } from './tabStrip/TabComponent';
export { TAB_STRIP_HEIGHT } from './tabStrip/tabStripModel';

const EnhancedTabManager = ({
  requestTabs = [],
  collectionTabs = [],
  schemaTabs = [],
  dbManagerTabs = [],
  taskManagerTabs = [],
  projectsTabs = [],
  documentTabs = [],
  activeTabId,
  activeTabType = 'request', // 'request' | 'collection' | 'schema' | 'db-manager-table' | 'task-manager-board' | 'projects-project' | 'document-editor'
  onTabChange,
  onTabClose,
  onCloseAllTabs,
  onCloseOtherTabs,
  onDuplicateTab,
  onNewRequestTab,
  requestKindsMenu = false,
  onTabsReorder,
  tabStripOrder = [],
  maxTabs = 15
}) => {
  const { t } = useTranslation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeDragId, setActiveDragId] = useState(null);
  const [tabSearchQuery, setTabSearchQuery] = useState('');
  const [tabSearchHighlightIndex, setTabSearchHighlightIndex] = useState(0);
  const [tabContextMenu, setTabContextMenu] = useState(null); // { x, y, tab }
  const dropdownRef = useRef(null);

  /**
   * Where the All-tabs panel is drawn, in VIEWPORT coordinates.
   *
   * It used to be `position: absolute` inside the tab strip, and the strip's
   * container is `overflow-hidden` — so the panel was clipped by it and read as
   * sitting under the sidebar (owner, 23.09.2026). A portal cannot be clipped
   * by an ancestor it no longer has, which is the same reason the variable
   * editor, ContextMenu and CompactSelectPicker all portal to `<body>`.
   *
   * Measured rather than assumed: the panel flips to the other side of its
   * button when there is no room, and that decision needs the real rect.
   */
  const [dropdownRect, setDropdownRect] = useState(null);
  const dropdownButtonRef = useRef(null);
  const tabSearchInputRef = useRef(null);
  const tabListRef = useRef(null);
  const tabContextMenuRef = useRef(null);
  const tabStripRef = useRef(null);

  // Re-measure while the panel is open: the strip scrolls and the window
  // resizes, and a portaled panel does not move with its button on its own.
  useLayoutEffect(() => {
    if (!showDropdown) {
      setDropdownRect(null);
      return undefined;
    }
    const measure = () => {
      const r = dropdownButtonRef.current?.getBoundingClientRect();
      if (r) setDropdownRect({ top: r.bottom, left: r.left, right: r.right, bottom: r.bottom });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [showDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          dropdownButtonRef.current && !dropdownButtonRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (tabContextMenuRef.current && !tabContextMenuRef.current.contains(event.target)) {
        setTabContextMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const tabMaps = useMemo(() => ({
    request: requestTabs,
    collection: collectionTabs,
    schema: schemaTabs,
    'db-manager-table': dbManagerTabs,
    'task-manager-board': taskManagerTabs,
    'projects-project': projectsTabs,
    'document-editor': documentTabs,
  }), [requestTabs, collectionTabs, schemaTabs, dbManagerTabs, taskManagerTabs, projectsTabs, documentTabs]);

  const effectiveStripOrder = useMemo(() => {
    if (tabStripOrder?.length) return tabStripOrder;
    return buildDefaultTabStripOrder(tabMaps);
  }, [tabStripOrder, tabMaps]);

  // Get all tabs combined in strip order — memoized so filteredTabs doesn't recompute every render
  const allTabs = useMemo(
    () => effectiveStripOrder
      .map((entry) => resolveTabFromEntry(entry, tabMaps))
      .filter(Boolean),
    [effectiveStripOrder, tabMaps]
  );

  const tabMatchesSearch = useCallback(
    (tab, q) => {
      const needle = q.trim().toLowerCase();
      if (!needle) return true;
      const title = getTabTitle(tab, t).toLowerCase();
      if (title.includes(needle)) return true;
      if (tab.type === 'request') {
        const method = (tab.method || 'GET').toLowerCase();
        if (method.includes(needle)) return true;
        if (tab.url && String(tab.url).toLowerCase().includes(needle)) return true;
        if (tab.name && String(tab.name).toLowerCase().includes(needle)) return true;
      } else {
        if (tab.name && String(tab.name).toLowerCase().includes(needle)) return true;
      }
      return false;
    },
    [t]
  );

  const filteredTabs = useMemo(
    () => allTabs.filter((tab) => tabMatchesSearch(tab, tabSearchQuery)),
    [allTabs, tabSearchQuery, tabMatchesSearch]
  );

  const selectFilteredTab = useCallback((tab) => {
    if (!tab) return;
    onTabChange(tab.id, tab.type);
    setShowDropdown(false);
  }, [onTabChange]);

  useEffect(() => {
    if (!showDropdown) {
      setTabSearchQuery('');
      setTabSearchHighlightIndex(0);
    }
  }, [showDropdown]);

  useEffect(() => {
    setTabSearchHighlightIndex(0);
  }, [tabSearchQuery]);

  useEffect(() => {
    setTabSearchHighlightIndex((i) => {
      if (!filteredTabs.length) return 0;
      return Math.min(i, filteredTabs.length - 1);
    });
  }, [filteredTabs.length]);

  useEffect(() => {
    if (!showDropdown) return;
    const id = requestAnimationFrame(() => tabSearchInputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [showDropdown]);

  useEffect(() => {
    if (!showDropdown) return;
    const el = tabListRef.current?.querySelector('[data-tab-search-highlighted="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [tabSearchHighlightIndex, showDropdown, filteredTabs]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = effectiveStripOrder.findIndex(
      (entry) => getTabStripSortableId(entry) === active.id
    );
    const newIdx = effectiveStripOrder.findIndex(
      (entry) => getTabStripSortableId(entry) === over.id
    );
    if (oldIdx === -1 || newIdx === -1) return;
    onTabsReorder?.(arrayMove(effectiveStripOrder, oldIdx, newIdx));
  }, [effectiveStripOrder, onTabsReorder]);

  useEffect(() => {
    if (!tabStripRef.current || !activeTabId) return;
    const strip = tabStripRef.current;
    const raf = requestAnimationFrame(() => {
      const activeEl = strip.querySelector('[data-tab-active="true"]');
      if (!activeEl) return;
      const stripRect = strip.getBoundingClientRect();
      const elRect = activeEl.getBoundingClientRect();
      if (elRect.left < stripRect.left) {
        strip.scrollBy({ left: elRect.left - stripRect.left - 4, behavior: 'smooth' });
      } else if (elRect.right > stripRect.right) {
        strip.scrollBy({ left: elRect.right - stripRect.right + 4, behavior: 'smooth' });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [activeTabId, activeTabType]);

  const sortableItemIds = useMemo(() => allTabs.map(getSortableTabId), [allTabs]);

  const handleTabContextMenu = useCallback((x, y, tab) => {
    const menuWidth = 192; // min-w-44 = 176px + small buffer
    const clampedX = Math.min(x, window.innerWidth - menuWidth);
    setTabContextMenu({ x: clampedX, y, tab });
  }, []);

  return (
    // One height for every strip (Collections, DB Manager, Boards, Projects, Documents, and
    // the Terminal's own strip): it used to come from the tabs inside, so a strip with NO
    // tabs — DB Manager before a table is opened — collapsed to 24 px.
    <div className="flex items-center border-b border-gray-200 bg-gray-50 min-h-0 app-tabs-border" style={{ minHeight: TAB_STRIP_HEIGHT }}>

      {/* Visible Tabs */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={DNDKIT_MODIFIERS}
        autoScroll={{ threshold: { x: 0.15, y: 0 } }}
        onDragStart={(e) => setActiveDragId(e.active.id)}
        onDragEnd={(event) => { setActiveDragId(null); handleDragEnd(event); }}
        onDragCancel={() => setActiveDragId(null)}
      >
        <SortableContext items={sortableItemIds} strategy={horizontalListSortingStrategy}>
          <div ref={tabStripRef} className="flex min-w-0 etm-tabs-strip">
            {allTabs.map(tab => (
              <SortableTab key={getSortableTabId(tab)} id={getSortableTabId(tab)} pinned={tab.pinned}>
                <TabComponent
                  tab={tab}
                  activeTabId={activeTabId}
                  activeTabType={activeTabType}
                  requestTabCount={requestTabs.length}
                  onTabChange={onTabChange}
                  onTabClose={onTabClose}
                  onContextMenu={handleTabContextMenu}
                  t={t}
                />
              </SortableTab>
            ))}
          </div>
        </SortableContext>
        <DragOverlay dropAnimation={null} modifiers={DNDKIT_MODIFIERS}>
          {activeDragId ? (() => {
            const draggedTab = allTabs.find((tab) => getSortableTabId(tab) === activeDragId);
            return draggedTab ? (
              <TabComponent
                tab={draggedTab}
                activeTabId={activeTabId}
                activeTabType={activeTabType}
                requestTabCount={requestTabs.length}
                onTabChange={onTabChange}
                onTabClose={onTabClose}
                onContextMenu={handleTabContextMenu}
                t={t}
              />
            ) : null;
          })() : null}
        </DragOverlay>
      </DndContext>

      {/* Right end of the strip — the same two controls in EVERY strip:
            +   a new tab; where the strip holds request tabs it opens the menu (HTTP ·
                GraphQL · WebSocket · SSE · gRPC). Absent where new tabs open elsewhere
                (Boards, Projects, Documents).
            ˅   the list of all open tabs (it used to be a "☰ (n)" button at the LEFT).
            Order [+] [˅] — the owner tried [˅] [+] on 20.09.2026 and went back. */}
      <div className="flex-shrink-0 flex items-center">
        {onNewRequestTab && (requestKindsMenu ? (
          <NewRequestKindMenu onPick={onNewRequestTab} />
        ) : (
          <Tooltip content={t('tabs.newRequestTab')} side="bottom">
            <button
              type="button"
              onClick={() => onNewRequestTab()}
              className="ml-2 py-1 rounded-lg bg-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-200 dark:hover:text-zinc-200 dark:hover:bg-zinc-700 transition-colors"
            >
              <Plus size={11} />
            </button>
          </Tooltip>
        ))}
        {/* Its own positioned box, NOT the flex row: an absolutely positioned child of a
            centred flex container is centred too — the panel opened 67 px above the window,
            header and search out of sight. */}
        <div className="relative flex-shrink-0">
        <Tooltip content={t('tabs.allTabsCount', { count: allTabs.length })} side="bottom" disabled={showDropdown}>
          <button
            type="button"
            ref={dropdownButtonRef}
            aria-haspopup="listbox"
            aria-expanded={showDropdown}
            aria-label={t('tabs.allTabs')}
            onClick={() => setShowDropdown(!showDropdown)}
            className="mx-2 py-1 rounded-lg bg-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-200 dark:hover:text-zinc-200 dark:hover:bg-zinc-700 transition-colors"
          >
            <ChevronDown size={11} />
          </button>
        </Tooltip>

        {/* Dropdown Menu — PORTALED to <body>.
            The tab strip's container is `overflow-hidden` (App.js), so an
            absolutely positioned panel was clipped by it and read as sitting
            under the sidebar. A portal has no such ancestor. */}
        {showDropdown && dropdownRect && createPortal(
          <div
            ref={dropdownRef}
            // the SAME surface as the "+" menu beside it and the context menus (dark: zinc-900, not the
            // slightly lighter `bg-white` override)
            className="fixed w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50 flex flex-col overflow-hidden dark:border-zinc-700 dark:bg-zinc-900"
            // Under the arrow. Usually the chevron is at the far right and the panel grows to the
            // left; in a strip with no tabs it is at the far LEFT — see utils/dropdownSide.
            // Viewport coordinates now, not offsets inside the strip.
            style={{
              top: dropdownRect.top + 8,
              maxHeight: 'min(420px, calc(100vh - 100px))',
              ...(() => {
                const side = dropdownSide(dropdownButtonRef.current?.getBoundingClientRect(), 288);
                return 'left' in side
                  ? { left: dropdownRect.left }
                  : { right: Math.max(8, window.innerWidth - dropdownRect.right) };
              })(),
            }}
          >
            {/* No title row ("All tabs (n)"): the search IS the header, Close all sits at its right. */}
            <div className="w-full flex-shrink-0 border-b border-gray-100 px-3 py-2">
              <div className="flex w-full min-w-0 items-center gap-2">
                <Search size={14} className="text-gray-400 flex-shrink-0" aria-hidden />
                <input
                  ref={tabSearchInputRef}
                  type="text"
                  value={tabSearchQuery}
                  onChange={(e) => setTabSearchQuery(e.target.value)}
                  placeholder={t('tabs.searchTabs')}
                  className="tab-dropdown-search-input min-w-0 w-full flex-1 bg-transparent text-gray-900 placeholder-gray-400 border-none p-0 focus:outline-none"
                  autoComplete="off"
                  aria-label={t('tabs.searchTabs')}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.stopPropagation();
                      setShowDropdown(false);
                      return;
                    }

                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setTabSearchHighlightIndex((i) =>
                        filteredTabs.length ? (i + 1) % filteredTabs.length : 0
                      );
                      return;
                    }

                    if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setTabSearchHighlightIndex((i) =>
                        filteredTabs.length ? (i - 1 + filteredTabs.length) % filteredTabs.length : 0
                      );
                      return;
                    }

                    if (e.key === 'Enter') {
                      e.preventDefault();
                      selectFilteredTab(filteredTabs[tabSearchHighlightIndex]);
                    }
                  }}
                />
                <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center">
                  {tabSearchQuery ? (
                    <button
                      type="button"
                      className="flex h-full w-full items-center justify-center rounded bg-transparent p-0 cursor-pointer text-gray-900 hover:opacity-70 focus:outline-none"
                      aria-label={t('sidebar.clearSearch')}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.stopPropagation();
                        setTabSearchQuery('');
                        tabSearchInputRef.current?.focus();
                      }}
                    >
                      <X size={14} strokeWidth={2} aria-hidden />
                    </button>
                  ) : null}
                </div>
                {typeof onCloseAllTabs === 'function' && (
                  <Tooltip content={t('tabs.closeAllTitle')} side="bottom">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCloseAllTabs();
                      setShowDropdown(false);
                    }}
                    className="tab-manager-close-all"
                    aria-label={t('tabs.closeAll')}
                  >
                    {/* an icon, quiet until hovered — the red "Close all" pill shouted in a menu that is
                        otherwise icon + name; the Tooltip around it says what it does */}
                    <ListX size={14} aria-hidden />
                  </button>
                  </Tooltip>
                )}
              </div>
            </div>
            <div ref={tabListRef} className="panel-scroll flex-1 min-h-0 py-2" style={{ overflowX: 'hidden' }}>
              {filteredTabs.length === 0 ? (
                <div className="px-3 py-6 text-center text-xs text-gray-500">{t('tabs.noMatchingTabs')}</div>
              ) : (
                filteredTabs.map((tab, index) => {
                  const active = activeTabId === tab.id && activeTabType === tab.type;
                  const highlighted = index === tabSearchHighlightIndex;
                  const title = getTabTitle(tab, t);
                  const tooltip = getTabTooltip(tab, t);
                  const method = getTabMethod(tab);

                  return (
                    <div
                      key={`${tab.type}-${tab.id}`}
                      data-tab-search-highlighted={highlighted ? 'true' : 'false'}
                      onMouseEnter={() => setTabSearchHighlightIndex(index)}
                      onClick={() => selectFilteredTab(tab)}
                      className={`px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-1 min-w-0 group ${
                        active ? 'bg-blue-50 border-r-2 border-blue-500' : highlighted ? 'bg-gray-100' : ''
                      }`}
                    >
                      {tab.type === 'request' ? (
                        <RequestTabBadge tab={tab} method={method} wide />
                      ) : tab.type === 'collection' ? (
                        <CollectionTabBadge tab={tab} wide />
                      ) : tab.type === 'schema' ? (
                        <span className="flex-shrink-0 w-12 flex items-center">
                          <Braces size={14} className="text-sky-600" aria-label="Schema" />
                        </span>
                      ) : tab.type === 'db-manager-table' ? (
                        <span className="flex-shrink-0 w-12 flex items-center">
                          <Database size={14} className="text-emerald-600" aria-label="Table" />
                        </span>
                      ) : tab.type === 'task-manager-board' ? (
                        <span className="flex-shrink-0 w-12 flex items-center">
                          <Kanban size={14} className="text-indigo-600" aria-label="Board" />
                        </span>
                      ) : tab.type === 'projects-project' ? (
                        <span className="flex-shrink-0 w-12 flex items-center">
                          <FolderKanban size={14} className="text-violet-600" aria-label="Project" />
                        </span>
                      ) : tab.type === 'document-editor' ? (
                        <span className="flex-shrink-0 w-12 flex items-center">
                          <FileText size={14} className="text-violet-600" aria-label="Document" />
                        </span>
                      ) : null}

                      {/* No colour class: the title inherits from
                          .etm-tab--active / .etm-tab--inactive, which define
                          both themes. Hardcoding text-gray-900 meant dark mode
                          hit `html.dark .text-gray-900 { color: #f3f4f6
                          !important }`, so every inactive tab was painted as
                          brightly as the active one. */}
                      <Tooltip content={tooltipContent(tooltip)} side="left" disabled={!tooltip}>
                        <span
                          className="flex-1 min-w-0 text-xs truncate"
                          aria-label={typeof title === 'string' ? title : undefined}
                        >
                          {title}
                        </span>
                      </Tooltip>

                      {isRequestTabCloseable(tab, requestTabs.length) && (
                        <Tooltip
                          content={tab.hasUnsavedChanges ? t('tabs.closeUnsaved') : t('tabs.closeTab')}
                          side="left"
                        >
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onTabClose(tab.id, tab.type);
                          }}
                          className={`tab-bar-close-btn flex-shrink-0 cursor-pointer rounded bg-transparent p-1 transition-colors ${
                            tab.hasUnsavedChanges
                              ? 'tab-bar-close-btn--unsaved opacity-100'
                              : 'opacity-0 group-hover:opacity-100'
                          }`}
                          aria-label={tab.hasUnsavedChanges ? t('tabs.closeUnsavedAria') : t('tabs.closeTabAria')}
                        >
                          <TabCloseIcon unsaved={tab.hasUnsavedChanges} size={12} />
                        </button>
                        </Tooltip>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )}
        </div>
      </div>

      {/* Tab right-click context menu */}
      {tabContextMenu && (
        <div
          ref={tabContextMenuRef}
          className="fixed z-50 min-w-40 animate-fadeIn rounded-lg border border-gray-200 bg-white py-2 shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
          style={{ left: tabContextMenu.x, top: tabContextMenu.y }}
        >
          {onNewRequestTab && (
            <button
              type="button"
              onClick={() => { onNewRequestTab(); setTabContextMenu(null); }}
              className="ctx-menu-btn"
            >
              <Plus size={14} />
              <span>New Tab</span>
            </button>
          )}
          {/* DB Manager tabs duplicate as well — opening the same table twice
              is how you compare two filters, or keep a reference query open
              beside one you are editing. */}
          {(tabContextMenu.tab.type === 'request'
            || (tabContextMenu.tab.type === 'db-manager-table' && tabContextMenu.tab.view !== 'overview'))
            && onDuplicateTab && (
            <button
              type="button"
              onClick={() => { onDuplicateTab(tabContextMenu.tab.id); setTabContextMenu(null); }}
              className="ctx-menu-btn"
            >
              <Copy size={14} />
              <span>Duplicate Tab</span>
            </button>
          )}
          <div className="my-1 border-t border-gray-100 dark:border-zinc-700" />
          {onCloseOtherTabs && (
            <button
              type="button"
              onClick={() => { onCloseOtherTabs(tabContextMenu.tab.id, tabContextMenu.tab.type); setTabContextMenu(null); }}
              className="ctx-menu-btn"
            >
              <Layers size={14} />
              <span>Close Others</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => { onTabClose(tabContextMenu.tab.id, tabContextMenu.tab.type); setTabContextMenu(null); }}
            className="ctx-menu-btn"
          >
            <X size={14} />
            <span>Close Tab</span>
          </button>
          {onCloseAllTabs && (
            <button
              type="button"
              onClick={() => { onCloseAllTabs(); setTabContextMenu(null); }}
              className="ctx-menu-btn ctx-menu-btn--danger"
            >
              <X size={14} />
              <span>Close All Tabs</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default EnhancedTabManager;
